import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Upload, Download, Images } from "lucide-react";
import CsvImportDialog from "../../components/CsvImportDialog";
import BulkVehiclePhotosDialog from "../../components/BulkVehiclePhotosDialog";
import { exportCsv } from "../../lib/csvExport";

const EMPTY = { vehicle_number:"", vehicle_type:"Ertiga", make:"", model:"", seating_capacity:6, ac:true, fuel_type:"Diesel", current_km:0, status:"available", photo_url:"" };

const CSV_SCHEMA = [
  { key: "vehicle_number", header: "vehicle_number", type: "str", required: true },
  { key: "vehicle_type",   header: "vehicle_type",   type: "str", required: true },
  { key: "make",           header: "make",           type: "str" },
  { key: "model",          header: "model",          type: "str" },
  { key: "seating_capacity", header: "seating_capacity", type: "int", required: true },
  { key: "ac",             header: "ac",             type: "bool", default: true },
  { key: "fuel_type",      header: "fuel_type",      type: "str", default: "Diesel" },
  { key: "current_km",     header: "current_km",     type: "int", default: 0 },
  { key: "status",         header: "status",         type: "str", default: "available" },
  { key: "photo_url",      header: "photo_url",      type: "str" },
];
const CSV_SAMPLE = { vehicle_number:"MH12-AB-1234", vehicle_type:"Tempo Traveller", make:"Force", model:"Traveller 3350", seating_capacity:17, ac:true, fuel_type:"Diesel", current_km:12000, status:"available", photo_url:"" };

export default function AdminVehicles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);

  const load = () => api.get("/vehicles").then(r=>setItems(r.data));
  useEffect(()=>{ load().catch(()=>{}); }, []);

  const save = async () => {
    try {
      const payload = {...form, seating_capacity: parseInt(form.seating_capacity), current_km: parseInt(form.current_km)||0};
      if (editing) await api.put(`/vehicles/${editing}`, {...payload, id: editing});
      else await api.post("/vehicles", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete vehicle?")) return; await api.delete(`/vehicles/${id}`); load(); toast.success("Deleted"); };
  const edit = (v) => { setEditing(v.id); setForm(v); setOpen(true); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-3xl font-display font-black tracking-tight">Vehicles</h1>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={()=>setPhotosOpen(true)} variant="outline" data-testid="veh-bulk-photos-btn" disabled={items.length===0}><Images className="w-4 h-4 mr-1" /> Bulk Photos</Button>
          <Button onClick={()=>exportCsv({filename:"vehicles",headers:CSV_SCHEMA,rows:items})} variant="outline" data-testid="veh-export-csv-btn" disabled={items.length===0}><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button onClick={()=>setCsvOpen(true)} variant="outline" data-testid="veh-import-csv-btn"><Upload className="w-4 h-4 mr-1" /> Import CSV</Button>
          <Button onClick={()=>{setEditing(null);setForm(EMPTY);setOpen(true);}} className="bg-[#ea580c] hover:bg-orange-700" data-testid="add-vehicle-btn"><Plus className="w-4 h-4 mr-1" /> Add Vehicle</Button>
        </div>
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(v => (
          <div key={v.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`vehicle-item-${v.id}`}>
            {v.photo_url && <img src={v.photo_url} alt={v.vehicle_type} className="w-full aspect-video object-cover" />}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-lg">{v.vehicle_number}</div>
                  <div className="text-sm text-slate-500">{v.vehicle_type} · {v.make} {v.model}</div>
                </div>
                <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100 capitalize">{v.status}</div>
              </div>
              <div className="mt-3 text-xs text-slate-600">{v.seating_capacity} seats · {v.ac?"AC":"Non-AC"} · {v.fuel_type}</div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={()=>edit(v)} data-testid={`edit-vehicle-${v.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={()=>del(v.id)} data-testid={`delete-vehicle-${v.id}`}><Trash2 className="w-3 h-3 mr-1 text-red-500" /> Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?"Edit":"Add"} Vehicle</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Vehicle Number</Label><Input value={form.vehicle_number} onChange={e=>setForm({...form,vehicle_number:e.target.value})} data-testid="veh-number" /></div>
            <div><Label>Vehicle Type</Label><Input value={form.vehicle_type} onChange={e=>setForm({...form,vehicle_type:e.target.value})} placeholder="Ertiga / Tempo Traveller / Bus…" data-testid="veh-type" /></div>
            <div><Label>Make</Label><Input value={form.make} onChange={e=>setForm({...form,make:e.target.value})} /></div>
            <div><Label>Model</Label><Input value={form.model} onChange={e=>setForm({...form,model:e.target.value})} /></div>
            <div><Label>Seating Capacity</Label><Input type="number" value={form.seating_capacity} onChange={e=>setForm({...form,seating_capacity:e.target.value})} /></div>
            <div><Label>Fuel</Label>
              <Select value={form.fuel_type} onValueChange={v=>setForm({...form,fuel_type:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Diesel","Petrol","CNG","EV"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Current KM</Label><Input type="number" value={form.current_km} onChange={e=>setForm({...form,current_km:e.target.value})} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v=>setForm({...form,status:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["available","reserved","on_trip","maintenance","inactive"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2"><Label>AC</Label><Switch checked={form.ac} onCheckedChange={v=>setForm({...form,ac:v})} data-testid="veh-ac" /></div>
            <div className="md:col-span-2">
              <Label>Vehicle Photo</Label>
              <div className="flex flex-col gap-2">
                <input type="file" accept="image/*" onChange={e=>{
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)."); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm(f=>({...f, photo_url: reader.result}));
                  reader.readAsDataURL(file);
                }} data-testid="veh-photo-file" className="text-xs" />
                <Input value={form.photo_url?.startsWith("data:") ? "(uploaded from device)" : (form.photo_url||"")} onChange={e=>setForm({...form,photo_url:e.target.value})} placeholder="Or paste image URL" data-testid="veh-photo-url" />
                {form.photo_url && <img src={form.photo_url} alt="preview" className="w-full max-h-40 object-cover rounded-lg border" />}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#ea580c] hover:bg-orange-700" data-testid="veh-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Vehicles"
        endpoint="/vehicles/bulk-import"
        schema={CSV_SCHEMA}
        sampleRow={CSV_SAMPLE}
        onImported={load}
      />

      <BulkVehiclePhotosDialog
        open={photosOpen}
        onOpenChange={setPhotosOpen}
        vehicles={items}
        onDone={load}
      />
    </div>
  );
}
